"use client";

import * as React from "react";
import { Icon } from "@/components/ui/icon";

import { Container } from "@/app/_components/layout/container";
import { testimonials } from "@/lib/content/homepage";
import { cn } from "@/lib/utils";

const avatarTones = ["bg-clarus-rose", "bg-clarus-green", "bg-clarus-blue"] as const;

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function Testimonials() {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(true);

  const updateNav = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateNav();
    el.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);
    return () => {
      el.removeEventListener("scroll", updateNav);
      window.removeEventListener("resize", updateNav);
    };
  }, [updateNav]);

  const scrollByCards = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLLIElement>("li");
    const step = first ? first.offsetWidth + 24 : 320;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <section
      aria-label="Kundenstimmen"
      className="bg-clarus-purple text-clarus-dark py-20 md:py-28"
    >
      <Container>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-display-lg text-clarus-dark">
            {testimonials.heading}
          </h2>
          <p className="text-subline mt-4 text-clarus-dark/80">
            {testimonials.subline}
          </p>
        </div>
      </Container>

      <div className="mt-12 md:mt-16 w-full">
        <div
          ref={scrollRef}
          className="overflow-x-auto scroll-smooth py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Kundenstimmen-Karussell"
          aria-roledescription="Karussell"
        >
          {/* Cards extend to the viewport edges so no purple strips show on
              the sides, and the next testimonial peeks past the right.
              The `after:` pseudo acts as a trailing flex item that the
              browser correctly includes in scrollWidth (unlike trailing
              margin/padding, which Chrome clips inside scroll containers).
              `-ml-6` cancels the flex `gap` so the spacer's width is the
              actual breathing room after the last card. */}
          <ul className="flex snap-x snap-mandatory gap-6 pl-5 pr-2 sm:pl-8 sm:pr-3 lg:pl-12 lg:pr-4 after:content-[''] after:block after:shrink-0 after:-ml-6 after:w-4 sm:after:w-5 lg:after:w-8">
            {testimonials.items.map((t, i) => (
              <li
                key={t.name}
                className="group/card flex shrink-0 snap-start flex-col rounded-card border-[1.5px] border-clarus-dark bg-white p-6 md:p-8 text-clarus-dark w-[340px] sm:w-[380px] md:w-[440px] lg:w-[calc((100vw-4rem-2.3*1.5rem)/3.3)] transition-[transform,box-shadow] duration-200 ease-[ease] hover:-translate-y-1 hover:shadow-[4px_4px_0_0_var(--color-clarus-dark)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div
                  className="flex gap-1"
                  aria-label={`${t.rating} von 5 Sternen`}
                >
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Icon
                      key={idx}
                      name="star"
                      filled
                      className="text-[16px] text-clarus-dark transition-[color,filter] duration-300 ease-out group-hover/card:text-clarus-yellow group-hover/card:[filter:drop-shadow(2px_2px_0_var(--color-clarus-dark))] motion-reduce:transition-none"
                      style={{
                        transitionDelay: `${idx * 60}ms`,
                      }}
                    />
                  ))}
                </div>
                <blockquote
                  className="mt-5 flex-1 text-clarus-dark/90"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.9375rem",
                    lineHeight: 1.55,
                  }}
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div className="mt-6 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={`${avatarTones[i % avatarTones.length]} flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-clarus-dark text-clarus-dark`}
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 800,
                      fontSize: "0.875rem",
                    }}
                  >
                    {getInitials(t.name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-clarus-dark"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 800,
                        fontSize: "0.9375rem",
                      }}
                    >
                      {t.name}
                    </p>
                    {t.role ? (
                      <p
                        className="text-clarus-dark/60 leading-snug"
                        style={{
                          fontFamily: "var(--font-sans)",
                          fontSize: "0.8125rem",
                        }}
                      >
                        {"href" in t && t.href ? (
                          <a
                            href={t.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-clarus-dark/30 underline-offset-2 transition-colors hover:text-clarus-dark hover:decoration-clarus-dark"
                          >
                            {t.role}
                          </a>
                        ) : (
                          t.role
                        )}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Container className="mt-8">
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            disabled={!canPrev}
            aria-label="Vorherige Stimme"
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-full text-clarus-yellow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
              canPrev
                ? "bg-clarus-dark/55 hover:bg-clarus-dark/75"
                : "bg-clarus-dark/35 text-clarus-yellow/80 cursor-not-allowed",
            )}
          >
            <Icon name="arrow_back" className="text-[20px]" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            disabled={!canNext}
            aria-label="Nächste Stimme"
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-full text-clarus-yellow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
              canNext
                ? "bg-clarus-dark hover:bg-clarus-dark/85"
                : "bg-clarus-dark/35 text-clarus-yellow/80 cursor-not-allowed",
            )}
          >
            <Icon name="arrow_forward" className="text-[20px]" />
          </button>
        </div>
      </Container>
    </section>
  );
}
